import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  healthColor,
  healthTone,
  type StatusTone,
  statusPresentation,
} from "~/features/automation/status";
import { cn } from "~/foundation/utils/cn";

const TONES: Record<StatusTone, string> = {
  neutral: "bg-bg-secondary text-text-secondary",
  accent: "bg-accent/12 text-accent",
  success: "bg-success/16 text-text",
  warning: "bg-warning/18 text-text",
  danger: "bg-danger/14 text-danger",
};

/** A small, non-interactive label. Everything in automation is categorical; this shows it. */
export function Pill({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: StatusTone;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center rounded-sm px-1.5 py-0.5 text-small font-medium",
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}

/** The server's status word, translated and toned in one place. */
export function StatusPill({ status, className }: { status: string; className?: string }) {
  const { t } = useTranslation();
  const { label, tone } = statusPresentation(status, t);
  return (
    <Pill tone={tone} className={className}>
      {label}
    </Pill>
  );
}

/** One number with its name under it — the unit of the analytics band. */
export function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-divider px-3 py-2">
      <span className="text-h3 font-semibold text-text tabular-nums">{value}</span>
      <span className="text-small text-text-secondary">{label}</span>
      {hint ? <span className="text-small text-text-tertiary">{hint}</span> : null}
    </div>
  );
}

/** The list shell every automation surface uses, so four screens look like one app. */
export function Rows({ children }: { children: ReactNode }) {
  return <ul className="flex flex-col rounded-md border border-divider">{children}</ul>;
}

export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <li
      className={cn(
        "flex items-center gap-3 border-b border-divider px-3 py-2.5 last:border-b-0",
        className,
      )}
    >
      {children}
    </li>
  );
}

/** A labelled value inside a detail dialog. */
export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-small text-text-tertiary">{label}</span>
      <span className="text-body text-text">{children}</span>
    </div>
  );
}

/**
 * A run/task/dispatch's health rollup — an infrastructure signal, never a status. Renders the
 * server's own note underneath, because the note is the answer ("worker stopped — this task
 * cannot fire"), not the badge.
 */
export function HealthPill({
  level,
  note,
  className,
}: {
  level: "ok" | "warn" | "error";
  note?: string | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const label = level === "error" ? t("Error") : level === "warn" ? t("Warning") : t("OK");
  return (
    <div className={cn("flex flex-col gap-0.5", className)}>
      <Pill tone={healthTone(level)}>{label}</Pill>
      {note ? <span className="text-small text-text-tertiary">{note}</span> : null}
    </div>
  );
}

/** A coloured left-border rail for a note strip — the health band, the degraded bar, etc. */
export function BandStrip({
  level,
  children,
  className,
}: {
  level: "ok" | "warn" | "error";
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn("rounded-sm border-s-4 bg-bg-secondary px-3 py-2.5", className)}
      style={{ borderInlineStartColor: healthColor(level) }}
    >
      {children}
    </div>
  );
}

/** One number + label, the unit of the Tasks front door's summary band. */
export function SummaryStat({
  label,
  value,
  tone = "neutral",
  hint,
}: {
  label: string;
  value: string;
  tone?: StatusTone;
  hint?: string;
}) {
  const TONE_TEXT: Record<StatusTone, string> = {
    neutral: "text-text",
    accent: "text-accent",
    success: "text-text",
    warning: "text-text",
    danger: "text-danger",
  };
  return (
    <div className="flex flex-col gap-0.5 rounded-md border border-divider px-3 py-2">
      <span className={cn("text-h3 font-semibold tabular-nums", TONE_TEXT[tone])}>{value}</span>
      <span className="text-small text-text-secondary">{label}</span>
      {hint ? <span className="text-small text-text-tertiary">{hint}</span> : null}
    </div>
  );
}

/**
 * The command ledger's token cell: never collapses the four categories into one number
 * (§A.1's core lesson — the v5 run was 96.7% cache-read, and a single "tokens" figure is
 * exactly what hid the finding). `label` is one of `in`/`cw`/`cr`/`out`.
 */
export function TokenCell({ label, value }: { label: string; value: string }) {
  return (
    <span className="whitespace-nowrap text-small text-text-secondary tabular-nums">
      {value} <span className="text-text-tertiary">{label}</span>
    </span>
  );
}
